import React, { useEffect, useState } from 'react';
import { Pencil, Wrench } from 'lucide-react';
import type { Ticket, UpdateTicketPayload } from '../../types';
import { Button, Card } from '../../shared/ui';
import { CHAMPS_EQUIPEMENT, EQUIPEMENT_MAX, changementsEquipement, saisieDepuisDossier, type SaisieEquipement } from './equipement';

// Mobile et tablette : cibles tactiles d'au moins 40 px ; rendu d'origine dès lg.
const TACTILE = 'min-h-[40px] lg:min-h-0';

/**
 * Bloc « Équipement » de la fiche : lecture, puis modification en ligne avec
 * enregistrement explicite. L'erreur du serveur reste affichée et la saisie
 * est conservée.
 */
export const FicheEquipement: React.FC<{
  ticket: Ticket;
  onEnregistrer: (changements: UpdateTicketPayload) => Promise<void>;
}> = ({ ticket, onEnregistrer }) => {
  const [edition, setEdition] = useState(false);
  const [saisie, setSaisie] = useState<SaisieEquipement>(() => saisieDepuisDossier(ticket));
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  // Hors édition, l'affichage suit le dossier (modification par un collègue).
  useEffect(() => {
    if (!edition) setSaisie(saisieDepuisDossier(ticket));
  }, [ticket, edition]);

  const changements = changementsEquipement(ticket, saisie);
  const modifie = Object.keys(changements).length > 0;

  const enregistrer = async () => {
    if (!modifie) { setEdition(false); return; }
    setEnCours(true);
    setErreur(null);
    try {
      await onEnregistrer(changements);
      setEdition(false);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : "L'enregistrement a échoué");
    } finally {
      setEnCours(false);
    }
  };

  const annuler = () => {
    setSaisie(saisieDepuisDossier(ticket));
    setErreur(null);
    setEdition(false);
  };

  const vide = CHAMPS_EQUIPEMENT.every(c => !ticket[c.cle]);

  return (
    <Card className="p-4 bg-brume">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-xs font-semibold text-graphite flex items-center gap-2">
          <Wrench className="w-4 h-4" />
          Équipement
        </h4>
        {!edition && (
          <Button variant="ghost" size="xs" onClick={() => setEdition(true)} className={TACTILE}>
            <Pencil className="w-3.5 h-3.5 mr-1" />Modifier
          </Button>
        )}
      </div>

      {edition ? (
        <form
          onSubmit={e => { e.preventDefault(); enregistrer(); }}
          className="space-y-3"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {CHAMPS_EQUIPEMENT.map(c => (
              <label key={c.cle} className="block">
                <span className="text-xs text-graphite">{c.libelle}</span>
                <input
                  value={saisie[c.cle]}
                  onChange={e => setSaisie(s => ({ ...s, [c.cle]: e.target.value }))}
                  maxLength={EQUIPEMENT_MAX}
                  className={`mt-1 block w-full rounded-lg border border-trait bg-white py-2 px-3 text-base sm:text-sm text-encre focus:border-sapin-600 focus:ring-2 focus:ring-sapin-600/20 focus:outline-none ${c.chiffres ? 'tnum' : ''}`}
                />
              </label>
            ))}
          </div>
          {erreur && <p role="alert" className="text-xs text-red-700">Non enregistré : {erreur}</p>}
          <div className="flex flex-col sm:flex-row gap-2">
            <Button type="submit" variant="primary" size="sm" disabled={enCours || !modifie} className={`w-full sm:w-auto ${TACTILE}`}>
              {enCours ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
            <Button variant="secondary" size="sm" onClick={annuler} disabled={enCours} className={`w-full sm:w-auto ${TACTILE}`}>Annuler</Button>
          </div>
        </form>
      ) : vide ? (
        <p className="text-sm text-graphite">Aucune information sur l'équipement.</p>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          {CHAMPS_EQUIPEMENT.map(c => ticket[c.cle] ? (
            <div key={c.cle} className="min-w-0 break-words">
              <span className="text-xs text-graphite">{c.libelle}</span>
              {c.cle === 'errorCode'
                ? <p className="tnum font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded inline-block">{ticket.errorCode}</p>
                : <p className={`font-medium text-encre ${c.chiffres ? 'tnum' : ''}`}>{ticket[c.cle]}</p>}
            </div>
          ) : null)}
        </div>
      )}
    </Card>
  );
};

export default FicheEquipement;
